import type { Category, TaskTemplate, Todo } from "../../domain/entities.ts";
import type { Member, Root, SubcategorySettings } from "../../domain/app-types.ts";
import { normalizeSortDirection, normalizeTaskSort } from "../tasks/selectors.ts";
import { openCollectionDialog, type CollectionDialogOptions } from "./collection-dialog-controller.ts";
import { eligibleSpaceDefaultMembers, eligibleSpaceDefaultTemplates, openSpaceDefaultsDialog } from "./space-defaults-controller.ts";

type IconRenderer = (name: string) => string;

export interface NavigationDialogRuntimeCapabilities {
  roots(): Root[];
  members(): Member[];
  templates(): TaskTemplate[];
  tasks(): Todo[];
  request(path: string, init: RequestInit): Promise<Response>;
  apiError(response: Response, fallback: string): Promise<string>;
  toast(message: string, error?: boolean): void;
  icon: IconRenderer;
}

export function collectionDialogOptions(mode: "create" | "edit", rootName: string, category?: Category): CollectionDialogOptions {
  return {
    mode, rootName, currentName: category?.name, currentView: category?.viewType || "list", currentColumns: category?.boardColumns || [],
    currentSort: normalizeTaskSort(category?.sortBy), currentSortDirection: normalizeSortDirection(category?.sortDirection, category?.sortBy),
    currentPageSize: category?.pageSize || 0, currentShowCompleted: !!category?.showCompleted, defaults: category || {},
  };
}

export function createNavigationDialogRuntime(capabilities: NavigationDialogRuntimeCapabilities) {
  async function collection(mode: "create" | "edit", rootName: string, category?: Category, rootId?: string): Promise<SubcategorySettings | null> {
    return openCollectionDialog(collectionDialogOptions(mode, rootName, category), {
      members: capabilities.members(), templates: capabilities.templates(), tasks: capabilities.tasks(), icon: capabilities.icon, toast: capabilities.toast, customFields: capabilities.roots().find(root => category || rootId ? root.id === (category?.rootId || rootId) : root.name === rootName)?.task_defaults?.custom_fields,
    });
  }

  function spaceDefaults(id: string): void {
    const root = capabilities.roots().find(item => item.id === id);
    if (!root) return;
    openSpaceDefaultsDialog(root, eligibleSpaceDefaultMembers(capabilities.members(), id), eligibleSpaceDefaultTemplates(capabilities.templates(), id), {
      request: capabilities.request, apiError: capabilities.apiError, toast: capabilities.toast, currentRoot: () => capabilities.roots().find(root => root.id === id),
    });
  }

  return { collection, spaceDefaults };
}
